import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  where,
} from 'firebase/firestore'

import { db } from 'utils/firebase'
import { createPartialUser } from 'utils/user/createPartialUser'

import { Completion } from 'types/completion'
import { Data } from 'types/query'

import { formatDocSnapshot, formatQuerySnapshot } from './formatFirestoreResults'

const DOC = 'completions'

export const fetchCompletions = async ({
  programId,
  userId,
}: {
  programId?: string
  userId?: string
}): Promise<Array<Completion>> => {
  if (!programId || !userId) {
    throw new Error('Error fetching completions')
  }
  let baseQuery = query(collection(db, DOC), where('post.program.id', '==', programId))
  baseQuery = query(baseQuery, where('user.id', '==', userId))
  const querySnapshot = await getDocs(baseQuery)
  return formatQuerySnapshot(querySnapshot)
}

export const addHighFive = async (data: Data) => {
  if (!data || !data?.user || !data?.completionId)
    throw new Error('No data passed into addHighFive')

  const { user, completionId } = data
  const partialUser = createPartialUser(user)
  const highFivesRef = collection(db, 'completions', completionId, 'highFives')
  const highFiveRef = doc(highFivesRef, user.id)
  const highFive = await getDoc(highFiveRef)
  if (highFive.exists()) {
    // Swallow this error since the action was already performed.
    console.error(`User has already high fived this Completion ${completionId}`)
    return true
  }
  await setDoc(highFiveRef, partialUser)
  return true
}

export const removeHighFive = async (data: Data) => {
  if (!data || !data?.user || !data?.completionId)
    throw new Error('No data passed into removeHighFive')

  const { user, completionId } = data
  const highFivesRef = collection(db, 'completions', completionId, 'highFives')
  const highFiveRef = doc(highFivesRef, user.id)
  await deleteDoc(highFiveRef)
  return true
}

export const isHighFived = async (data: Data) => {
  if (!data || !data?.userId || !data?.completionId)
    throw new Error('No data passed into isHighFived')

  const { userId, completionId } = data
  const highFiveRef = doc(db, 'completions', completionId, 'highFives', userId)
  const highFive = await getDoc(highFiveRef)
  return highFive.exists()
}

export const fetchCompletionById = async (
  completionId: string,
): Promise<Completion | undefined> => {
  if (!completionId) throw new Error('No data passed into fetchCompletionById')

  try {
    const completionRef = doc(db, 'completions', completionId)
    const completionSnap = await getDoc(completionRef)
    const completion: Completion = formatDocSnapshot(completionSnap)
    if (!completion.highFivedCount) {
      completion.highFivedCount = 0
    }
    if (!completion.commentsCount) {
      completion.commentsCount = 0
    }

    return completion
  } catch (error) {
    // Not all events are completions. Although this will be called for all events.
    if (error.message !== 'Document does not exist') {
      throw error
    } else {
      return undefined
    }
  }
}
